import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../App';
import { Play, Calendar, ArrowRight, Clock, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LiveSessionsPage() {
  const [streamSettings, setStreamSettings] = useState(null);
  const [schedule, setSchedule] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [streamRes, scheduleRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/livestream/settings`),
          fetch(`${BACKEND_URL}/api/schedule`)
        ]);
        
        if (streamRes.ok) setStreamSettings(await streamRes.json());
        if (scheduleRes.ok) setSchedule(await scheduleRes.json());
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden" style={{
        background: 'linear-gradient(135deg, #1a4d2e 0%, #2d5f3f 50%, #1a4d2e 100%)'
      }}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/20 border border-red-600/40 rounded-full mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            <span className="text-red-400 text-sm font-bold uppercase tracking-wider">Live Now</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-heading font-bold text-white mb-6">
            Live Poker Sessions
          </h1>
          <p className="text-xl text-white/80 max-w-3xl mx-auto mb-10">
            Watch expert players break down strategy, analyze hands, and answer your questions in real-time.
          </p>

          <Link
            to="/live"
            className="inline-flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-10 py-5 rounded-xl text-lg font-bold shadow-2xl transition-all transform hover:scale-105"
          >
            <Play className="w-6 h-6" />
            Join Live Stream
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-20 bg-card/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-heading font-bold text-center mb-12">
            What You'll Experience
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-red-600/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Play className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">Real-Time Strategy</h3>
              <p className="text-muted-foreground">Watch pros analyze hands as they happen and learn their decision-making process.</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Interactive Q&A</h3>
              <p className="text-muted-foreground">Ask questions during the stream and get instant answers from experienced players.</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-green-600/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">Weekly Sessions</h3>
              <p className="text-muted-foreground">Join us multiple times per week for different topics and skill levels.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Schedule */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-4xl font-heading font-bold mb-2">Upcoming Sessions</h2>
              <p className="text-muted-foreground text-lg">Mark your calendar for these live discussions</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {schedule.map(item => (
              <Link
                to="/booking"
                key={item.schedule_id}
                className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 hover:shadow-lg transition-all cursor-pointer"
                data-testid={`live-schedule-card-${item.schedule_id}`}
              >
                <div className="flex items-center gap-2 text-red-500 font-semibold mb-4">
                  <Calendar className="w-5 h-5" />
                  <span className="text-sm">
                    {new Date(item.scheduled_at).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-muted-foreground mb-4">{item.description}</p>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(item.scheduled_at).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </div>
                  <span>•</span>
                  <span>{item.duration_minutes} min</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-primary/10 to-red-600/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-heading font-bold mb-6">
            Ready to Join the Action?
          </h2>
          <p className="text-xl text-muted-foreground mb-10">
            Become a member to access all live sessions and archived content.
          </p>
          <Link
            to="/membership"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-black px-10 py-5 rounded-xl text-lg font-bold shadow-lg transition-all"
          >
            Get Started
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
